// NEW_FILE_JUSTIFIED: Extraction progress step for bulk import
import { useState, useEffect } from 'react';
import { Progress } from '@/components/ui/progress';
import { Sparkles, FileText, Clock } from 'lucide-react';
import type { IBulkImportExtractionStepProps } from '@/@types/bulkImport';

// Estimated seconds per file for AI extraction (PDF with ~50 policies takes ~5-6 min)
const ESTIMATED_SECONDS_PER_FILE = 330;
const MIN_ESTIMATED_SECONDS = 60;

const STAGES = [
  'Uploading files to storage...',
  'Preparing documents for AI analysis...',
  'AI is reading and understanding document structure...',
  'Extracting policy data from tables...',
  'Identifying policy numbers, dates, and premiums...',
  'Processing claims history and additional fields...',
  'Deriving payment status from balance data...',
  'Finalizing extraction results...',
];

function formatTime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

export function BulkImportExtractionStep({ fileNames }: IBulkImportExtractionStepProps) {
  const [elapsed, setElapsed] = useState(0);
  // Capture mount time via lazy state initializer so Date.now() is called only once
  const [startTime] = useState<number>(() => Date.now());

  const estimatedTotal = Math.max(
    MIN_ESTIMATED_SECONDS,
    fileNames.length * ESTIMATED_SECONDS_PER_FILE
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  // Progress calculation: fast start, slow in middle, asymptotic approach to 95%
  // Uses a logarithmic curve so it never reaches 100% (that happens when extraction completes)
  const rawProgress = Math.min(95, (elapsed / estimatedTotal) * 85 + Math.log1p(elapsed) * 3);
  const progress = Math.round(rawProgress);

  // Stage based on progress percentage
  const stageIndex = Math.min(STAGES.length - 1, Math.floor((progress / 100) * STAGES.length));
  const currentStage = STAGES[stageIndex];

  const remaining = Math.max(0, estimatedTotal - elapsed);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold text-foreground">Extracting Policies</h2>
        <p className="text-sm text-muted-foreground">
          AI is analyzing your files and extracting policy data. This may take up to 5 minutes for
          large files.
        </p>
      </div>

      {/* Progress bar */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Sparkles className="w-4 h-4 animate-pulse" aria-hidden="true" />
            <span>{currentStage}</span>
          </div>
          <span className="text-sm font-medium text-foreground">{progress}%</span>
        </div>
        <Progress value={progress} className="h-2" />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" aria-hidden="true" />
            <span>Elapsed: {formatTime(elapsed)}</span>
          </div>
          {remaining > 0 && elapsed > 5 && <span>~{formatTime(remaining)} remaining</span>}
        </div>
      </div>

      {/* File list */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">
          Processing {fileNames.length} file{fileNames.length !== 1 ? 's' : ''}
        </p>
        {fileNames.map(name => (
          <div key={name} className="flex items-center gap-2 py-1.5 px-3 rounded-lg bg-muted/50">
            <FileText className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
            <span className="text-sm text-foreground truncate">{name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
